// Projets de travaux (M14, lot L4-C, D-146) : liste avec les deux avancements côte à côte (physique sur
// les jalons, financier sur les mandats payés), retard automatique signalé, carte des projets localisés.
import type { ResumeProjet } from '@cc/contrats';
import { Bouton, Carte, EtatVide, Lien, PuceEtat, Tableau, type ColonneTableau } from '@cc/ui';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { useProjets } from './api';
import { FormulaireProjet } from './FormulairesTravaux';
import { libelleProjet, texteAvancementFinancier, texteAvancementPhysique, TON_PROJET } from './logique';
import styles from '../achats/achats.module.css';

const LIBREVILLE: [number, number] = [9.4544, 0.3901];

export function PageProjets() {
  const { contexte } = useSession();
  const projets = useProjets();
  const naviguer = useNavigate();
  const [creation, setCreation] = useState(false);
  const liste = projets.data?.elements ?? [];
  const localises = liste.filter((p) => p.position);
  const colonnes: ColonneTableau<ResumeProjet>[] = [
    { id: 'numero', entete: 'Projet', enTeteLigne: true, masquable: false, cellule: (p) => <Lien href={`/travaux/${p.id}`} className="cc-mono">{p.numero}</Lien>, valeurTri: (p) => p.numero },
    { id: 'intitule', entete: 'Intitulé', cellule: (p) => p.intitule },
    { id: 'lieu', entete: 'Lieu', cellule: (p) => p.adresse ?? '' },
    { id: 'periode', entete: 'Période prévue', cellule: (p) => `${p.debut_prevu} au ${p.fin_prevue}` },
    { id: 'physique', entete: 'Avancement physique', cellule: (p) => texteAvancementPhysique(p.avancement_physique), valeurTri: (p) => p.avancement_physique.taux },
    { id: 'financier', entete: 'Avancement financier', cellule: (p) => texteAvancementFinancier(p.avancement_financier, p.fictif), valeurTri: (p) => p.avancement_financier.taux ?? -1 },
    { id: 'etat', entete: 'État', cellule: (p) => <span className={styles.actesLigne}><PuceEtat ton={TON_PROJET[p.etat]} libelle={libelleProjet(p.etat)} />{p.en_retard ? <PuceEtat ton="retard" libelle="En retard" /> : null}</span> },
  ];
  return (
    <>
      <EnTetePage
        etiquette="Travaux et chantiers"
        titre="Projets de travaux"
        description="Avancement physique (jalons validés) et avancement financier (payé sur montant contractuel) sont suivis séparément ; un jalon échu sans validation met le chantier en retard."
        actions={peut(contexte, 'travaux', 'creer') ? <Bouton variante="principal" icone={Plus} onClick={() => setCreation(true)}>Nouveau projet</Bouton> : null}
      />
      <EtatRequete chargement={projets.isPending} erreur={projets.error} onReessayer={() => void projets.refetch()} lot="L4-C" fonction="Les projets de travaux">
        <section className={styles.bloc} aria-labelledby="titre-projets">
          <h2 id="titre-projets">Projets</h2>
          {liste.length === 0 ? <EtatVide titre="Aucun projet"><p>Aucun projet de travaux n&apos;est enregistré.</p></EtatVide> : <Tableau legende="Projets de travaux" colonnes={colonnes} lignes={liste} cleLigne={(p) => p.id} cleColonnes="travaux-projets" />}
        </section>
        <section className={styles.bloc} aria-labelledby="titre-carte-projets">
          <h2 id="titre-carte-projets">Carte des chantiers</h2>
          {localises.length === 0 ? <p className={styles.detail}>Aucun projet localisé.</p> : (
            <Carte
              titre="Carte des projets de travaux"
              centre={localises[0]?.position ?? LIBREVILLE}
              zoom={12}
              couches={[
                { id: 'retard', libelle: 'En retard', forme: 'triangle', ton: 'rouge', points: localises.filter((p) => p.en_retard).map((p) => ({ id: p.id, longitude: p.position![0], latitude: p.position![1], libelle: `${p.numero} : ${p.intitule}` })) },
                { id: 'suivi', libelle: 'Sans retard', forme: 'cercle', ton: 'bleu', points: localises.filter((p) => !p.en_retard).map((p) => ({ id: p.id, longitude: p.position![0], latitude: p.position![1], libelle: `${p.numero} : ${p.intitule}` })) },
              ]}
              onPoint={(_couche, point) => void naviguer(`/travaux/${point.id}`)}
            />
          )}
        </section>
      </EtatRequete>
      {creation ? <FormulaireProjet onFermer={() => setCreation(false)} onCree={(id) => { setCreation(false); void naviguer(`/travaux/${id}`); }} /> : null}
    </>
  );
}
